
import { WebClientMethod } from './webClientMethod';
import { WebClientRequest } from './webClientRequest';
import { WebClientResponse } from './webClientResponse';

/**
 * Takes a WebClientRequest and executes the command with the given parameters and returns the response.
 */
export interface WebClientRequester {
  send<TResponse>(webClientRequest: WebClientRequest<Object|void, TResponse>): PromiseLike<WebClientResponse<TResponse>>;
}

/**
 * Executes a Web Client method with request parameters and returns a response.
 * It is usually created by coupling a WebClientRequester and a WebClientMethod.
 */
export interface WebClientSender<TRequest, TResponse> {
  (params: TRequest): PromiseLike<WebClientResponse<TResponse>>;
}

export interface VoidWebClientSender<TResponse> {
  (): PromiseLike<WebClientResponse<TResponse>>;
}

/**
 * Takes a requester and adds helper methods to the requester to allow separation
 * between the method and params
 */
export class WebClientRequesterWrapper implements WebClientRequester {
  public constructor(
    private requester: WebClientRequester
  ) { }

  public static createSender<TResponse>(requester: WebClientRequester, method: WebClientMethod<void, TResponse>): VoidWebClientSender<TResponse>
  public static createSender<TParams, TResponse>(requester: WebClientRequester, method: WebClientMethod<TParams, TResponse>): WebClientSender<TParams, TResponse>
  public static createSender<TParams, TResponse>(requester: WebClientRequester, method: WebClientMethod<TParams, TResponse>): WebClientSender<TParams, TResponse> {
    return (new WebClientRequesterWrapper(requester)).createSender(method);
  }

  public createSender<TResponse>(method: WebClientMethod<void, TResponse>): VoidWebClientSender<TResponse>
  public createSender<TParams, TResponse>(method: WebClientMethod<TParams, TResponse>): WebClientSender<TParams, TResponse>
  public createSender<TParams, TResponse>(method: WebClientMethod<TParams, TResponse>): WebClientSender<TParams, TResponse> {
    return (params: TParams) => this.sendMethod(method, params);
  }

  public static sendMethod<TResponse>(requester: WebClientRequester, method: WebClientMethod<void, TResponse>): PromiseLike<WebClientResponse<TResponse>>
  public static sendMethod<TParams, TResponse>(requester: WebClientRequester, method: WebClientMethod<TParams, TResponse>, params: TParams): PromiseLike<WebClientResponse<TResponse>>
  public static sendMethod<TParams, TResponse>(requester: WebClientRequester, method: WebClientMethod<TParams, TResponse>, params?: TParams): PromiseLike<WebClientResponse<TResponse>> {
    return (new WebClientRequesterWrapper(requester)).sendMethod(method, params);
  }

  public sendMethod<TResponse>(method: WebClientMethod<void, TResponse>): PromiseLike<WebClientResponse<TResponse>>
  public sendMethod<TParams, TResponse>(method: WebClientMethod<TParams, TResponse>, params: TParams): PromiseLike<WebClientResponse<TResponse>>
  public sendMethod<TParams, TResponse>(method: WebClientMethod<TParams, TResponse>, params?: TParams): PromiseLike<WebClientResponse<TResponse>> {
    return this.send({ method, params });
  }

  public send<TResponse>(webClientRequest: WebClientRequest<Object|void, TResponse>): PromiseLike<WebClientResponse<TResponse>> {
    return this.requester.send(webClientRequest);
  }
}
